import type {
  CustomerPlayExclusion,
  CustomerSpendingLimit,
  Nullable,
  Pence,
} from '@/types'
import {
  isCustomerPlayExclusion,
  isCustomerSpendingLimit,
} from '@/utils/type-guard-utils'
import { DateTime } from 'luxon'
import { defineStore } from 'pinia'

import { useUserStore } from './user'

type CustomerSafetyStoreState = {
  customerSafety: Map<
    CustomerSpendingLimit['__type'] | CustomerPlayExclusion['__type'],
    CustomerSpendingLimit | CustomerPlayExclusion
  >
}

export const useCustomerSafetyStore = defineStore('customerSafety', {
  state: (): CustomerSafetyStoreState => ({
    customerSafety: new Map(),
  }),

  getters: {
    /**
     * Determines whether the user is currently blocked from playing due to player safety settings (spending limits or self-exclusion).
     *
     * @returns {boolean} True if the user is blocked, false otherwise.
     */
    hasPlayerSafetyLockout(): boolean {
      return this.activeCustomerSafety.length > 0
    },

    /**
     * Checks if the user is currently blocked from playing due to player safety settings (spending limits or self-exclusion).
     *
     * @returns {Array} An array of active customer safety settings that are currently blocking the user, or an empty array if none are blocking.
     */
    activeCustomerSafety(): Array<
      CustomerSpendingLimit | CustomerPlayExclusion
    > {
      const userStore = useUserStore()

      if (userStore.isLoggedOut || this.customerSafety.size === 0) {
        return []
      }

      return Array.from(this.customerSafety.values()).filter(
        (customerSafetySetting) => {
          if (isCustomerSpendingLimit(customerSafetySetting)) {
            return (
              customerSafetySetting.is_active &&
              customerSafetySetting.current_spend -
                (userStore.user?.credit.totals.overall ?? 0) >
                customerSafetySetting.spending_limit
            )
          }

          if (isCustomerPlayExclusion(customerSafetySetting)) {
            return (
              customerSafetySetting.is_active &&
              DateTime.now().diff(customerSafetySetting.ends_at).milliseconds <=
                0
            )
          }

          return false
        }
      )
    },
  },

  actions: {
    /**
     * Checks if the user has reached their spending limit based on their customer safety settings.
     *
     * @param {Pence} amount The amount to check against the user's spending limit.
     * @returns {boolean} True if the user has reached or exceeded their spending limit, false otherwise.
     */
    checkCustomerSpendingLimit(amount: Pence): boolean {
      const spendingLimit = this.customerSafety.get('CustomerSpendingLimit')

      if (!spendingLimit || !isCustomerSpendingLimit(spendingLimit)) {
        return false
      }

      const userStore = useUserStore()

      return (
        spendingLimit.current_spend +
          amount -
          (userStore.user?.credit.totals.overall ?? 0) >=
        spendingLimit.spending_limit
      )
    },

    getFirstActiveCustomerSafety(
      amount: Nullable<Pence>
    ): CustomerSpendingLimit | CustomerPlayExclusion | null {
      if (amount && this.checkCustomerSpendingLimit(amount)) {
        return this.customerSafety.get('CustomerSpendingLimit') ?? null
      }

      return this.customerSafety.get('CustomerPlayExclusion') ?? null
    },
  },
})
